import { createFileRoute } from "@tanstack/react-router";
import { DashboardShell } from "@/components/DashboardShell";
import { PARTNER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import { VerificationBadge } from "@/components/StatusBadge";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { VerificationStatus } from "@/lib/mock-data";
import { SeoPreviewPanel } from "@/components/SeoPreviewPanel";
import { SeoChecklistPanel } from "@/components/SeoChecklistPanel";
import { buildSeo } from "@/lib/seo";
import { partnerProfileUrl, partnerSeoDescription, partnerSeoTitle } from "@/lib/partner-public";

export const Route = createFileRoute("/_authenticated/partner/profile")({
  head: () => ({
    meta: [
      { title: "Profile · Landoo Partner" },
      { name: "description", content: "Your Landoo partner profile." },
    ],
  }),
  component: PartnerProfile,
});

function PartnerProfile() {
  const { profile, refreshProfile } = useAuth();
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [phone, setPhone] = useState(profile?.phone ?? "");
  const [city, setCity] = useState(profile?.city ?? "");
  const [partnerType, setPartnerType] = useState(profile?.partner_type ?? "");
  const [saving, setSaving] = useState(false);

  // Live preview of the metadata that goes public when this profile is published.
  const businessName = (fullName || profile?.full_name || "Landoo Partner").trim();
  const seo = buildSeo({
    title: partnerSeoTitle(businessName, city || null),
    description: partnerSeoDescription({
      businessName,
      city: city || null,
      activeListings: 0,
      averageRating: 0,
      totalReviews: 0,
      description: partnerType || null,
    }),
    url: partnerProfileUrl(businessName, profile?.id ?? ""),
    image: profile?.avatar_url ?? null,
    imageKind: "partner",
    ogType: "profile",
  });

  const save = async () => {
    if (!profile) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ full_name: fullName, phone, city, partner_type: partnerType })
      .eq("id", profile.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Profile updated");
    await refreshProfile();
  };

  return (
    <DashboardShell title="Profile" role="Partner" items={PARTNER_NAV}>
      <div className="grid gap-4 lg:grid-cols-[minmax(0,36rem)_minmax(0,26rem)]">
        <div className="max-w-xl space-y-4 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Email</div>
            <div className="text-sm font-medium text-brown-dark">{profile?.email}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Verification
            </div>
            <div className="mt-1">
              <VerificationBadge
                status={(profile?.verification_status as VerificationStatus) ?? "Not Started"}
              />
            </div>
          </div>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              Full name
            </div>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} className={inp} />
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Phone</div>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={inp} />
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">City</div>
            <input value={city} onChange={(e) => setCity(e.target.value)} className={inp} />
          </label>
          <label className="block">
            <div className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">
              Partner type
            </div>
            <input
              value={partnerType}
              onChange={(e) => setPartnerType(e.target.value)}
              className={inp}
            />
          </label>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-coral px-4 py-2 text-sm font-semibold text-white shadow-soft disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>

        <div className="space-y-2 rounded-2xl border border-border bg-card p-5 shadow-soft">
          <p className="text-xs text-muted-foreground">
            This is how your public partner profile appears in search results and shared links once
            it is published.
          </p>
          <SeoChecklistPanel seo={seo} title="Pre-publish SEO checklist" />
          <SeoPreviewPanel seo={seo} />
        </div>
      </div>
    </DashboardShell>
  );
}

const inp =
  "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30";
